import React from 'react';
import Link from '../../../Shared/Link';
import theme from '../../../../styles/theme';
import ButtonLink from '../../../Shared/ButtonLink';
import { NavLink } from 'react-router-dom';
import CollapsibleItem from '../../../Shared/CollapsibleItem';
import CaretDown from '../../../Shared/Icons/CaretDown';
import UpdatedMobileMenuStyles from './UpdatedMobileMenu.styled';
import {
  SingleLinkProps,
  MultiLinkProps,
  defaultNavItems,
  defaultNavSection,
  defaultButtonLinks,
} from '../defaultNavData';

/**
 * Props for the `UpdatedMobileMenu` component.
 */
interface MobileMenuProps {
  /** Links for the top navigation section. */
  navSection?: SingleLinkProps[];
  /** Links for the main navigation items. */
  navItems?: MultiLinkProps[];
  /** Links for the buttons in the menu. */
  buttonLinks?: SingleLinkProps[];
}

/**
 * The `UpdatedMobileMenu` component renders the mobile menu with navigation links, collapsible items, and buttons.
 *
 * @param {MobileMenuProps} props - The props for the component.
 * @returns {JSX.Element} The rendered mobile menu component.
 */
const UpdatedMobileMenu: React.FC<MobileMenuProps> = ({
  navSection = defaultNavSection,
  navItems = defaultNavItems,
  buttonLinks = defaultButtonLinks,
}) => {
  return (
    <UpdatedMobileMenuStyles>
      {/* Top navigation section */}
      <div className='nav-section'>
        {navSection.map((item, index) => (
          <Link
            includeArrowIcon={false}
            to={item.to}
            className={`${item.gtmClassname ? item.gtmClassname : ''}`}
            key={`nav-section-link-mbl-${index}`} // Add a unique key
          >
            {item.title}
          </Link>
        ))}
      </div>

      {/* Main navigation items */}
      <div className='bottom-nav'>
        {navItems.map((item, index) =>
          item.links.length > 1 ? (
            <div className='nav-item' key={`nav-item-${index}`}>
              <CollapsibleItem
                title={item.title}
                iconColor={theme.colors.grey1}
              >
                <div className='dropdown'>
                  {item.links.map((link, linkIndex) => (
                    <NavLink
                      key={`dropdown-link-${index}-${linkIndex}`} // Add a unique key
                      to={link.to}
                      className='dropdown-link'
                    >
                      {link.title}
                    </NavLink>
                  ))}
                </div>
              </CollapsibleItem>
            </div>
          ) : (
            <div className='nav-item' key={`nav-item-${index}`}>
              <NavLink className='container nav-item' to={item.links[0].to}>
                {item.links[0].title || item.title}
                <CaretDown
                  className='caret-down'
                  height={20}
                  color={theme.colors.grey1}
                />
              </NavLink>
            </div>
          )
        )}
      </div>

      {/* Button links */}
      <div className='button-links'>
        {buttonLinks.map((item, index) => (
          <ButtonLink
            key={`button-link-${index}`} // Add a unique key
            to={item.to}
            appearance='dark'
            hoverAppearance='dark'
            size='small'
            isExternal={true}
            newTab={true}
            className={`apply-button ${item.gtmClassname ? item.gtmClassname : ''}`}
          >
            {item.title}
          </ButtonLink>
        ))}
      </div>
    </UpdatedMobileMenuStyles>
  );
};

export default UpdatedMobileMenu;
